import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah, formatDate } from "@/lib/format";
import { ListOrdered, ChevronRight, ImageUp } from "lucide-react";
import { UploadTransferProofDialog, hasTransfer } from "@/components/TransferProof";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PayStateBadge, PAY_STATUS_LABEL, SELECT_CLS, today } from "@/components/SalesShared";

export default function TransactionList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fl, setFl] = useState({ q: "", cat: "", start: today(), end: today() });
  const [upTx, setUpTx] = useState(null);
  const load = useCallback(() => {
    api.get("/transactions").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setFl((p) => ({ ...p, [k]: e.target.value }));
  const s = fl.q.trim().toLowerCase();
  const filtered = rows.filter((r) => (!fl.cat || r.pay_status === fl.cat)
    && (!fl.start || r.date >= fl.start) && (!fl.end || r.date <= fl.end)
    && (!s || [r.nota_number, r.customer_name, r.customer_phone].some((x) => (x || "").toLowerCase().includes(s))));
  const total = filtered.reduce((t, r) => t + (r.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><ListOrdered className="h-6 w-6 text-brand" /> Daftar Transaksi</h2>
        <p className="text-sm text-muted-foreground">Riwayat nota penjualan Anda. Klik baris untuk melihat detail, edit, atau ubah status bayar.</p>
      </div>
      <Card className="p-4 grid grid-cols-2 lg:grid-cols-5 gap-3 items-end" data-testid="txl-filters">
        <div><Label className="text-xs">Kategori</Label>
          <select value={fl.cat} onChange={set("cat")} className={`${SELECT_CLS} mt-1 h-9`} data-testid="txl-category">
            <option value="">Semua</option>
            {Object.entries(PAY_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" value={fl.start} onChange={set("start")} className="mt-1 h-9" data-testid="txl-start" /></div>
        <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" value={fl.end} onChange={set("end")} className="mt-1 h-9" data-testid="txl-end" /></div>
        <div className="col-span-2"><Label className="text-xs">Cari</Label><Input value={fl.q} onChange={set("q")} placeholder="Cari nota / pelanggan / HP" className="mt-1 h-9" data-testid="tx-search" /></div>
      </Card>
      <Card className="p-0 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b">
          <h3 className="font-semibold" style={{ fontFamily: "Outfit" }} data-testid="txl-count">Daftar Transaksi ({filtered.length})</h3>
          <p className="text-sm">Total <span className="font-mono font-semibold" data-testid="txl-total">{formatRupiah(total)}</span></p>
        </div>
        {loading ? <div className="p-6"><div className="h-4 rounded-md skeleton-shimmer" /></div> : filtered.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground" data-testid="tx-empty">Tidak ada transaksi pada filter ini.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tx-table">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground">
                <tr><th className="text-left px-4 py-2.5">Tanggal</th><th className="text-left px-4 py-2.5">Nota</th><th className="text-left px-4 py-2.5">Pelanggan</th>
                  <th className="text-left px-4 py-2.5">Status Bayar</th><th className="text-right px-4 py-2.5">Jumlah</th><th className="text-right px-4 py-2.5">Sisa</th><th className="px-4 py-2.5"></th></tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} onClick={() => navigate(`/transactions/${r.id}`)} className="border-t cursor-pointer hover:bg-secondary/60" data-testid={`tx-row-${r.id}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(r.date)}</td>
                    <td className="px-4 py-2.5 font-mono">{r.nota_number}</td>
                    <td className="px-4 py-2.5">{r.customer_name}<span className="block text-xs text-muted-foreground">{r.customer_phone}</span></td>
                    <td className="px-4 py-2.5">
                      <span className="font-medium">{PAY_STATUS_LABEL[r.pay_status]}</span>
                      {r.pay_status === "transfer" && <span className="block text-xs text-muted-foreground">{r.company_name}{r.target_account && <span className="block font-mono">{r.target_account.bank_name} {r.target_account.account_number}</span>}</span>}
                      {r.pay_status === "split" && <span className="block text-xs text-muted-foreground font-mono">{(r.payments || []).map((p) => `${p.method === "transfer" ? "TF" : "Cash"} ${formatRupiah(p.amount)}`).join(" + ")}</span>}
                      {r.pay_status === "kredit" && <span className="block mt-0.5"><PayStateBadge state={r.payment_state} /></span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono">{formatRupiah(r.amount)}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{formatRupiah(r.remaining)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-2">
                        {hasTransfer(r) && (
                          <button type="button" onClick={(e) => { e.stopPropagation(); setUpTx(r); }} title="Upload Bukti Transfer"
                            className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border whitespace-nowrap ${(r.transfer_proofs || []).length ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid={`txl-upload-proof-${r.id}`}>
                            <ImageUp className="h-3.5 w-3.5" /> {(r.transfer_proofs || []).length ? `Bukti (${r.transfer_proofs.length})` : "Upload Bukti Transfer"}
                          </button>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {upTx && <UploadTransferProofDialog tx={upTx} open={!!upTx} onOpenChange={(o) => { if (!o) setUpTx(null); }} onDone={load} />}
    </div>
  );
}
